import { useState, useCallback, useMemo } from "react";

import { useUpdateFolder } from "hooks/api/use-folders";
import { CampaignFolderDTO } from "generated/schemas";
import EditFolderModal from "containers/admin/clients/campaign/campaign-creative-page/folder-details/components/edit-folder-modal/edit-folder-modal";
import toast from "components/partials/toast/toast";
import { getErrorMessage } from "utilities";

export const useEditFolderModal = () => {
  const [editingFolder, setEditingFolder] = useState<CampaignFolderDTO | null>(null);

  const editFolderMutation = useUpdateFolder();

  const openEditFolder = useCallback((folder: CampaignFolderDTO) => {
    setEditingFolder(folder);
  }, []);

  const closeEditFolder = useCallback(() => {
    setEditingFolder(null);
  }, []);

  const handleEditFolder = useCallback(
    async (data: { name: string }) => {
      if (!editingFolder?.id) return;

      try {
        await editFolderMutation.mutateAsync({
          id: editingFolder.id,
          data: { name: data.name },
        });

        toast.success({
          data: {
            title: `Updated '${data.name}'`,
          },
        });
        closeEditFolder();
      } catch (error) {
        toast.error({
          data: {
            title: "Failed to update folder",
            message: getErrorMessage(error),
          },
        });
      }
    },
    [editingFolder?.id, editFolderMutation, closeEditFolder],
  );

  const editFolderModalMarkup = useMemo(() => {
    if (!editingFolder) return null;

    return (
      <EditFolderModal
        key={editingFolder.id}
        folderName={editingFolder.name}
        onCloseCallback={closeEditFolder}
        onConfirmCallback={handleEditFolder}
        isSubmitting={editFolderMutation.isPending}
      />
    );
  }, [editingFolder, closeEditFolder, handleEditFolder, editFolderMutation.isPending]);

  return {
    openEditFolder,
    closeEditFolder,
    editFolderModalMarkup,
  };
};
